/**
 * Admin Social Posts Scheduler Tab
 * Plan and schedule social media content
 */
import { useState, useEffect } from "react";
import { 
  Calendar, Clock, Plus, Edit2, Trash2, Eye, Send, 
  Facebook, Instagram, Image, FileText, CheckCircle, 
  XCircle, Loader2, Filter, Bell, AlertCircle, CalendarDays,
  ChevronLeft, ChevronRight, List, Download, Copy, Upload,
  Repeat, TrendingUp, BarChart2, Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { XIcon } from "../../lib/social";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: "facebook", name: "Facebook", icon: Facebook, color: "bg-blue-600" },
  { id: "instagram", name: "Instagram", icon: Instagram, color: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400" },
  { id: "x", name: "X (Twitter)", icon: XIcon, color: "bg-black" }
];

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  scheduled: "bg-blue-100 text-blue-700",
  published: "bg-green-100 text-green-700"
};

// Calendar View Component
const CalendarView = ({ calendarMonth, setCalendarMonth, calendarData, loadingCalendar, platforms, onDayClick, onQuickCreate }) => {
  const [year, month] = calendarMonth.split("-").map(Number);
  const [selectedDay, setSelectedDay] = useState(null);
  
  // Get calendar grid data
  const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month - 1, 1).getDay();
  
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  
  const prevMonth = () => {
    const newMonth = month === 1 ? 12 : month - 1;
    const newYear = month === 1 ? year - 1 : year;
    setCalendarMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
    setSelectedDay(null);
  };
  
  const nextMonth = () => {
    const newMonth = month === 12 ? 1 : month + 1;
    const newYear = month === 12 ? year + 1 : year;
    setCalendarMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
    setSelectedDay(null);
  };
  
  const today = new Date();
  const isToday = (day) => {
    return today.getDate() === day && 
           today.getMonth() === month - 1 && 
           today.getFullYear() === year;
  };
  
  const getDateString = (day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  
  const getPlatformIcon = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    if (!platform) return null;
    const Icon = platform.icon;
    return Icon;
  };

  const handleDayClick = (day) => {
    const dateStr = getDateString(day);
    setSelectedDay(selectedDay === day ? null : day);
    if (onDayClick) {
      onDayClick(dateStr, calendarData.posts_by_date?.[dateStr] || []);
    }
  };
  
  // Build calendar grid
  const calendarDays = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={prevMonth}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <CardTitle className="text-lg">
            {monthNames[month - 1]} {year}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={nextMonth}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loadingCalendar ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-stone-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="h-24 bg-stone-50 rounded-lg" />;
                }
                
                const dateStr = getDateString(day);
                const dayPosts = calendarData.posts_by_date?.[dateStr] || [];
                const todayClass = isToday(day) ? "ring-2 ring-amber-500" : "";
                const selectedClass = selectedDay === day ? "bg-amber-50 border-amber-300" : "bg-white";
                
                return (
                  <div 
                    key={day} 
                    onClick={() => handleDayClick(day)}
                    className={`h-24 border rounded-lg p-1 overflow-hidden ${todayClass} ${selectedClass} hover:shadow-md hover:border-amber-200 transition-all cursor-pointer`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${isToday(day) ? 'text-amber-600' : 'text-stone-700'}`}>
                        {day}
                      </span>
                      {dayPosts.length > 0 && (
                        <Badge variant="secondary" className="text-xs px-1 py-0">
                          {dayPosts.length}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-0.5 overflow-y-auto max-h-16">
                      {dayPosts.slice(0, 3).map((post, pIdx) => {
                        const statusColor = post.status === 'published' ? 'bg-green-500' : 
                                          post.status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-400';
                        return (
                          <div 
                            key={pIdx}
                            className="flex items-center gap-1 text-xs p-1 rounded bg-stone-50 truncate"
                            title={post.content}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${statusColor} flex-shrink-0`} />
                            <div className="flex gap-0.5 flex-shrink-0">
                              {post.platforms?.slice(0, 2).map(pId => {
                                const Icon = getPlatformIcon(pId);
                                return Icon ? <Icon key={pId} className="w-3 h-3 text-stone-500" /> : null;
                              })}
                            </div>
                            {post.scheduled_time && (
                              <span className="text-stone-400">{post.scheduled_time}</span>
                            )}
                          </div>
                        );
                      })}
                      {dayPosts.length > 3 && (
                        <div className="text-xs text-stone-400 text-center">
                          +{dayPosts.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-stone-500">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                Draft
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Scheduled
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Published
              </div>
            </div>

            {/* Selected Day Panel */}
            {selectedDay && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-stone-800">
                    {monthNames[month - 1]} {selectedDay}, {year}
                  </h4>
                  <Button 
                    size="sm" 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => onQuickCreate && onQuickCreate(getDateString(selectedDay))}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Quick Create
                  </Button>
                </div>
                {(calendarData.posts_by_date?.[getDateString(selectedDay)] || []).length > 0 ? (
                  <div className="space-y-2">
                    {calendarData.posts_by_date[getDateString(selectedDay)].map((post) => (
                      <div key={post.post_id} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            post.status === 'published' ? 'bg-green-500' : 
                            post.status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-400'
                          }`} />
                          <div className="flex gap-1">
                            {post.platforms?.map(pId => {
                              const Icon = getPlatformIcon(pId);
                              return Icon ? <Icon key={pId} className="w-4 h-4 text-stone-600" /> : null;
                            })}
                          </div>
                          <span className="text-sm text-stone-600 truncate max-w-[200px]">{post.content}</span>
                        </div>
                        <span className="text-xs text-stone-400">{post.scheduled_time || 'No time set'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 text-center py-2">No posts scheduled for this day</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Week View Component
const WeekView = ({ calendarMonth, setCalendarMonth, calendarData, loadingCalendar, platforms, onQuickCreate }) => {
  const [year, month] = calendarMonth.split("-").map(Number);
  const [weekOffset, setWeekOffset] = useState(0);
  
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  
  // Get the start of current week (Sunday)
  const getWeekStart = () => {
    const now = new Date();
    now.setDate(now.getDate() + (weekOffset * 7));
    const day = now.getDay();
    const diff = now.getDate() - day;
    return new Date(now.setDate(diff));
  };
  
  const weekStart = getWeekStart();
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });
  
  const getDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const getPlatformIcon = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    if (!platform) return null;
    return platform.icon;
  };
  
  const weekLabel = () => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (start.getMonth() === end.getMonth()) {
      return `${monthNames[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <CardTitle className="text-lg">{weekLabel()}</CardTitle>
            {weekOffset !== 0 && (
              <button 
                className="text-xs text-amber-600 hover:underline mt-1"
                onClick={() => setWeekOffset(0)}
              >
                Go to current week
              </button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loadingCalendar ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((date, idx) => {
              const dateStr = getDateString(date);
              const dayPosts = calendarData.posts_by_date?.[dateStr] || [];
              const todayClass = isToday(date) ? "ring-2 ring-amber-500 bg-amber-50" : "bg-white";
              
              return (
                <div key={dateStr} className={`${todayClass} border rounded-lg p-3 min-h-[200px]`}>
                  <div className="text-center mb-3 pb-2 border-b">
                    <p className="text-xs text-stone-500 uppercase">{dayNames[idx].slice(0, 3)}</p>
                    <p className={`text-2xl font-bold ${isToday(date) ? 'text-amber-600' : 'text-stone-800'}`}>
                      {date.getDate()}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    {dayPosts.map((post) => {
                      const statusColor = post.status === 'published' ? 'border-l-green-500' : 
                                        post.status === 'scheduled' ? 'border-l-blue-500' : 'border-l-gray-400';
                      return (
                        <div 
                          key={post.post_id}
                          className={`p-2 bg-stone-50 rounded border-l-4 ${statusColor}`}
                          title={post.content}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex gap-1">
                              {post.platforms?.map(pId => {
                                const Icon = getPlatformIcon(pId);
                                return Icon ? <Icon key={pId} className="w-3 h-3 text-stone-500" /> : null;
                              })}
                            </div>
                            <span className="text-xs text-amber-600 font-medium">{post.scheduled_time || ''}</span>
                          </div>
                          <p className="text-xs text-stone-600 line-clamp-2">{post.content}</p>
                        </div>
                      );
                    })}
                    
                    {dayPosts.length === 0 && (
                      <p className="text-xs text-stone-400 text-center py-4">No posts</p>
                    )}
                  </div>
                  
                  <button
                    onClick={() => onQuickCreate && onQuickCreate(dateStr)}
                    className="mt-2 w-full py-1 text-xs text-amber-600 hover:bg-amber-50 rounded border border-dashed border-amber-300 flex items-center justify-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminSocialPostsTab = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [dueTodayPosts, setDueTodayPosts] = useState([]);
  const [checkingReminders, setCheckingReminders] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list", "calendar", or "week"
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calendarData, setCalendarData] = useState({ posts_by_date: {} });
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState("");
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringPost, setRecurringPost] = useState(null);
  const [recurringFrequency, setRecurringFrequency] = useState("weekly");
  const [recurringOccurrences, setRecurringOccurrences] = useState(4);
  const [creatingRecurring, setCreatingRecurring] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [suggestedHashtags, setSuggestedHashtags] = useState(null);
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  const [formData, setFormData] = useState({
    content: "",
    platforms: [],
    scheduled_date: "",
    scheduled_time: "",
    hashtags: [],
    notes: "",
    status: "draft",
    images: [] // Array of {base64, mime_type} objects, max 4
  });
  const [hashtagInput, setHashtagInput] = useState("");

  useEffect(() => {
    fetchPosts();
    fetchStats();
    fetchDueToday();
    fetchTemplates();
  }, [filter]);

  useEffect(() => {
    if (viewMode === "calendar" || viewMode === "week") {
      fetchCalendarData();
    }
  }, [viewMode, calendarMonth]);

  const fetchPosts = async () => {
    try {
      const url = filter === "all" 
        ? `${API_URL}/api/social-posts`
        : `${API_URL}/api/social-posts?status=${filter}`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    setLoadingCalendar(true);
    try {
      const res = await fetch(`${API_URL}/api/social-posts/calendar?month=${calendarMonth}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setCalendarData(data);
    } catch (err) {
      console.error("Failed to fetch calendar data:", err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-posts/stats/overview`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const fetchDueToday = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-posts/due-today`, {
        credentials: 'include'
      });
      const data = await res.json();
      setDueTodayPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch due today:", err);
    }
  };

  const checkReminders = async () => {
    setCheckingReminders(true);
    try {
      const res = await fetch(`${API_URL}/api/social-posts/reminders/check`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.email_sent) {
        toast.success(`Reminder sent for ${data.posts_due} post(s)!`);
      } else if (data.posts_due === 0) {
        toast.info("No posts due today");
      } else {
        toast.warning("Couldn't send reminder email");
      }
      fetchDueToday();
    } catch (err) {
      console.error("Failed to check reminders:", err);
      toast.error("Failed to check reminders");
    } finally {
      setCheckingReminders(false);
    }
  };

  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const fetchSchedulerStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-posts/scheduler/status`, {
        credentials: 'include'
      });
      const data = await res.json();
      setSchedulerStatus(data);
    } catch (err) {
      console.error("Failed to fetch scheduler status:", err);
    }
  };

  // Quick create from calendar day click
  const handleQuickCreate = (dateStr) => {
    resetForm();
    setFormData(prev => ({ ...prev, scheduled_date: dateStr }));
    setShowModal(true);
  };

  // Export calendar as PDF
  const exportCalendarPDF = async () => {
    setExportingPDF(true);
    try {
      const [year, month] = calendarMonth.split("-").map(Number);
      const monthNames = ["January", "February", "March", "April", "May", "June", 
                          "July", "August", "September", "October", "November", "December"];
      
      // Get platform names
      const getPlatformName = (id) => PLATFORMS.find(p => p.id === id)?.name || id;
      
      // Build content for PDF
      let content = `
        <html>
        <head>
          <title>Social Media Schedule - ${monthNames[month - 1]} ${year}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #1c1917; }
            h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
            h2 { color: #44403c; margin-top: 30px; }
            .post { background: #fafaf9; border: 1px solid #e7e5e4; border-radius: 8px; padding: 15px; margin: 10px 0; }
            .post-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
            .platforms { color: #78716c; font-size: 12px; }
            .time { color: #d97706; font-weight: bold; }
            .content { color: #44403c; }
            .status { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; text-transform: uppercase; }
            .status-draft { background: #e7e5e4; color: #57534e; }
            .status-scheduled { background: #dbeafe; color: #1d4ed8; }
            .status-published { background: #dcfce7; color: #16a34a; }
            .no-posts { color: #a8a29e; font-style: italic; text-align: center; padding: 20px; }
            .footer { margin-top: 40px; text-align: center; color: #a8a29e; font-size: 12px; border-top: 1px solid #e7e5e4; padding-top: 20px; }
          </style>
        </head>
        <body>
          <h1>🐱 CatPals+ Social Media Schedule</h1>
          <h2>${monthNames[month - 1]} ${year}</h2>
      `;
      
      // Group posts by date and sort
      const sortedDates = Object.keys(calendarData.posts_by_date || {}).sort();
      
      if (sortedDates.length === 0) {
        content += '<p class="no-posts">No posts scheduled for this month</p>';
      } else {
        for (const dateStr of sortedDates) {
          const datePosts = calendarData.posts_by_date[dateStr];
          const dateObj = new Date(dateStr + 'T12:00:00');
          const formattedDate = dateObj.toLocaleDateString('en-GB', { 
            weekday: 'long', day: 'numeric', month: 'long' 
          });
          
          content += `<h3 style="color: #78716c; margin-top: 25px;">${formattedDate}</h3>`;
          
          for (const post of datePosts) {
            const platforms = post.platforms?.map(getPlatformName).join(', ') || 'Not specified';
            const statusClass = `status-${post.status || 'draft'}`;
            
            content += `
              <div class="post">
                <div class="post-header">
                  <span class="platforms">${platforms}</span>
                  <span class="time">${post.scheduled_time || 'No time set'}</span>
                </div>
                <p class="content">${post.content}</p>
                <span class="status ${statusClass}">${post.status || 'draft'}</span>
              </div>
            `;
          }
        }
      }
      
      content += `
          <div class="footer">
            Generated on ${new Date().toLocaleDateString('en-GB', { 
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}
          </div>
        </body>
        </html>
      `;
      
      // Open print dialog using Blob URL instead of document.write
      const blob = new Blob([content], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after the window loads
      if (printWindow) {
        printWindow.onload = () => {
          URL.revokeObjectURL(blobUrl);
          printWindow.print();
        };
      }
      
      toast.success("PDF export opened - use Print to PDF to save");
    } catch (err) {
      console.error("Failed to export PDF:", err);
      toast.error("Failed to export schedule");
    } finally {
      setExportingPDF(false);
    }
  };

  // Duplicate a post (copy all content but clear the date)
  const duplicatePost = (post) => {
    setEditingPost(null);
    // Convert legacy single image to images array
    const images = [];
    if (post.image_base64) {
      images.push({ base64: post.image_base64, mime_type: post.image_mime_type || "image/jpeg" });
    }
    if (post.additional_images) {
      images.push(...post.additional_images);
    }
    
    setFormData({
      content: post.content || "",
      platforms: post.platforms || [],
      scheduled_date: "", // Clear date so user picks a new one
      scheduled_time: post.scheduled_time || "",
      hashtags: post.hashtags || [],
      notes: post.notes || "",
      status: "draft", // Start as draft
      images: images
    });
    setSuggestedHashtags(null);
    setShowModal(true);
    toast.info("Post duplicated - pick a new date and save");
  };

  useEffect(() => {
    fetchSchedulerStatus();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-posts/templates`, {
        credentials: 'include'
      });
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Template name:", "My Template");
    if (!name) return;
    
    try {
      await fetch(`${API_URL}/api/social-posts/templates`, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name,
          content: formData.content,
          hashtags: formData.hashtags,
          platforms: formData.platforms
        })
      });
      toast.success("Template saved!");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template");
    }
  };

  const loadTemplate = async (template) => {
    // Mark template as used
    try {
      await fetch(`${API_URL}/api/social-posts/templates/${template.template_id}/use`, {
        method: "POST",
        credentials: 'include'
      });
    } catch (err) {
      console.error("Failed to track template use:", err);
    }
    
    setFormData(prev => ({
      ...prev,
      content: template.content || "",
      hashtags: template.hashtags || [],
      platforms: template.platforms || []
    }));
    setShowTemplates(false);
    toast.success(`Loaded template: ${template.name}`);
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    
    try {
      await fetch(`${API_URL}/api/social-posts/templates/${templateId}`, {
        method: "DELETE",
        credentials: 'include'
      });
      fetchTemplates();
      toast.success("Template deleted");
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  // Bulk Upload Posts from CSV
  const handleBulkUpload = async () => {
    if (!bulkCsvText.trim()) {
      toast.error("Please paste CSV data");
      return;
    }
    
    setUploadingBulk(true);
    try {
      // Parse CSV (simple parser)
      const lines = bulkCsvText.trim().split("\n");
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      
      const posts = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map(v => v.trim());
        const post = {};
        headers.forEach((header, idx) => {
          post[header] = values[idx] || "";
        });
        posts.push(post);
      }
      
      const res = await fetch(`${API_URL}/api/social-posts/bulk-upload`, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ posts })
      });
      
      const data = await res.json();
      
      if (data.created_count > 0) {
        toast.success(`Created ${data.created_count} posts!`);
        fetchPosts();
        setShowBulkUpload(false);
        setBulkCsvText("");
      }
      
      if (data.error_count > 0) {
        toast.error(`${data.error_count} rows had errors`);
      }
    } catch (err) {
      toast.error("Failed to upload posts");
    } finally {
      setUploadingBulk(false);
    }
  };

  // Create Recurring Posts
  const openRecurringModal = (post) => {
    setRecurringPost(post);
    setRecurringFrequency("weekly");
    setRecurringOccurrences(4);
    setShowRecurring(true);
  };

  const handleCreateRecurring = async () => {
    if (!recurringPost) return;
    
    setCreatingRecurring(true);
    try {
      const res = await fetch(`${API_URL}/api/social-posts/${recurringPost.post_id}/recur`, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          frequency: recurringFrequency,
          occurrences: recurringOccurrences,
          start_date: recurringPost.scheduled_date
        })
      });
      
      const data = await res.json();
      toast.success(data.message);
      fetchPosts();
      fetchCalendarData();
      setShowRecurring(false);
    } catch (err) {
      toast.error("Failed to create recurring posts");
    } finally {
      setCreatingRecurring(false);
    }
  };

  // Fetch Analytics Preview
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await fetch(`${API_URL}/api/social-posts/analytics/preview`, {
        credentials: 'include'
      });
      const data = await res.json();
      setAnalyticsData(data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const openAnalytics = () => {
    setShowAnalytics(true);
    fetchAnalytics();
  };

  // Fetch hashtag suggestions based on content
  const fetchHashtagSuggestions = async () => {
    if (!formData.content.trim()) {
      setSuggestedHashtags(null);
      return;
    }
    
    setLoadingHashtags(true);
    try {
      const res = await fetch(`${API_URL}/api/social-posts/hashtags/suggest`, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ content: formData.content })
      });
      const data = await res.json();
      setSuggestedHashtags(data);
    } catch (err) {
      console.error("Failed to fetch hashtag suggestions:", err);
    } finally {
      setLoadingHashtags(false);
    }
  };

  // Add image to carousel (max 4)
  const handleAddImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (formData.images.length >= 4) {
      toast.error("Maximum 4 images allowed");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, { base64, mime_type: file.type }]
      }));
    };
    reader.readAsDataURL(file);
    e.target.value = ""; // Reset input
  };

  // Remove image from carousel
  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Add suggested hashtag
  const addSuggestedHashtag = (tag) => {
    if (!formData.hashtags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, tag]
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      setFormData(prev => ({
        ...prev,
        image_base64: base64,
        image_mime_type: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const addHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.trim().startsWith("#") 
        ? hashtagInput.trim() 
        : `#${hashtagInput.trim()}`;
      if (!formData.hashtags.includes(tag)) {
        setFormData(prev => ({
          ...prev,
          hashtags: [...prev.hashtags, tag]
        }));
      }
      setHashtagInput("");
    }
  };

  const removeHashtag = (tag) => {
    setFormData(prev => ({
      ...prev,
      hashtags: prev.hashtags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPost 
        ? `${API_URL}/api/social-posts/${editingPost.post_id}`
        : `${API_URL}/api/social-posts`;
      
      // Convert images array to legacy format for backward compatibility
      const submitData = {
        ...formData,
        image_base64: formData.images[0]?.base64 || "",
        image_mime_type: formData.images[0]?.mime_type || "",
        additional_images: formData.images.slice(1) // Images 2-4
      };
      
      const res = await fetch(url, {
        method: editingPost ? "PUT" : "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        setShowModal(false);
        resetForm();
        fetchPosts();
        fetchStats();
        toast.success(editingPost ? "Post updated!" : "Post created!");
      }
    } catch (err) {
      console.error("Failed to save post:", err);
      toast.error("Failed to save post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    // Convert legacy single image to images array
    const images = [];
    if (post.image_base64) {
      images.push({ base64: post.image_base64, mime_type: post.image_mime_type || "image/jpeg" });
    }
    if (post.additional_images) {
      images.push(...post.additional_images);
    }
    
    setFormData({
      content: post.content || "",
      platforms: post.platforms || [],
      scheduled_date: post.scheduled_date || "",
      scheduled_time: post.scheduled_time || "",
      hashtags: post.hashtags || [],
      notes: post.notes || "",
      status: post.status || "draft",
      images: images
    });
    setSuggestedHashtags(null);
    setShowModal(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    
    try {
      await fetch(`${API_URL}/api/social-posts/${postId}`, {
        method: "DELETE",
        credentials: 'include'
      });
      fetchPosts();
      fetchStats();
    } catch (err) {
      console.error("Failed to delete post:", err);
    }
  };

  const markAsPublished = async (postId) => {
    try {
      await fetch(`${API_URL}/api/social-posts/${postId}`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: "published" })
      });
      fetchPosts();
      fetchStats();
    } catch (err) {
      console.error("Failed to update post:", err);
    }
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      content: "",
      platforms: [],
      scheduled_date: "",
      scheduled_time: "",
      hashtags: [],
      notes: "",
      status: "draft",
      images: []
    });
    setHashtagInput("");
    setSuggestedHashtags(null);
  };

  const openPreview = (post) => {
    setPreviewPost(post);
    setShowPreview(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not scheduled";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short"
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-social-posts-tab">
      {/* Due Today Alert */}
      {dueTodayPosts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    {dueTodayPosts.length} Post(s) Due Today!
                  </p>
                  <p className="text-sm text-amber-600">
                    Ready to publish on your social media accounts
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                onClick={checkReminders}
                disabled={checkingReminders}
              >
                {checkingReminders ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Send Reminder Email
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-stone-900">{stats.total || 0}</p>
            <p className="text-sm text-stone-500">Total Posts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-600">{stats.drafts || 0}</p>
            <p className="text-sm text-stone-500">Drafts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.scheduled || 0}</p>
            <p className="text-sm text-stone-500">Scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.published || 0}</p>
            <p className="text-sm text-stone-500">Published</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.upcoming_week || 0}</p>
            <p className="text-sm text-stone-500">This Week</p>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Reminder Status */}
      {schedulerStatus && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <p className="font-medium text-purple-800">
                    Auto-Reminder: {schedulerStatus.scheduler_running ? 'Active' : 'Inactive'}
                  </p>
                  <p className="text-sm text-purple-600">
                    Daily emails at {schedulerStatus.daily_reminder_time}
                    {schedulerStatus.jobs?.[0]?.next_run && (
                      <span className="ml-2">
                        • Next: {new Date(schedulerStatus.jobs[0].next_run).toLocaleString('en-GB', { 
                          weekday: 'short', 
                          day: 'numeric', 
                          month: 'short',
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Bell className="w-5 h-5 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-stone-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Posts</option>
              <option value="draft">Drafts</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </select>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 flex items-center gap-1 text-sm ${
                viewMode === "list" ? "bg-amber-100 text-amber-800" : "bg-white text-stone-600 hover:bg-stone-50"
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`px-3 py-2 flex items-center gap-1 text-sm ${
                viewMode === "week" ? "bg-amber-100 text-amber-800" : "bg-white text-stone-600 hover:bg-stone-50"
              }`}
            >
              <Calendar className="w-4 h-4" />
              Week
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-3 py-2 flex items-center gap-1 text-sm ${
                viewMode === "calendar" ? "bg-amber-100 text-amber-800" : "bg-white text-stone-600 hover:bg-stone-50"
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Month
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowBulkUpload(true)}
            className="text-stone-600"
          >
            <Upload className="w-4 h-4 mr-1" />
            Bulk Upload
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={openAnalytics}
            className="text-stone-600"
          >
            <BarChart2 className="w-4 h-4 mr-1" />
            Analytics
          </Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      {/* Calendar/Week View */}
      {viewMode === "calendar" ? (
        <>
          {/* Export Button */}
          <div className="flex justify-end mb-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportCalendarPDF}
              disabled={exportingPDF}
              className="text-stone-600"
            >
              {exportingPDF ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export Schedule
            </Button>
          </div>
          <CalendarView 
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
            calendarData={calendarData}
            loadingCalendar={loadingCalendar}
            platforms={PLATFORMS}
            onQuickCreate={handleQuickCreate}
          />
        </>
      ) : viewMode === "week" ? (
        <WeekView 
          calendarMonth={calendarMonth}
          setCalendarMonth={setCalendarMonth}
          calendarData={calendarData}
          loadingCalendar={loadingCalendar}
          platforms={PLATFORMS}
          onQuickCreate={handleQuickCreate}
        />
      ) : (
        /* Posts List */
        loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-semibold text-stone-700 mb-2">No posts yet</h3>
            <p className="text-stone-500 mb-4">Start planning your social media content</p>
            <Button onClick={() => setShowModal(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {posts.map(post => (
            <Card key={post.post_id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Image Preview */}
                  {post.image_base64 && (
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-stone-100">
                      <img 
                        src={`data:${post.image_mime_type};base64,${post.image_base64}`}
                        alt="Post"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={STATUS_COLORS[post.status]}>
                          {post.status}
                        </Badge>
                        {post.platforms?.map(p => {
                          const platform = PLATFORMS.find(pl => pl.id === p);
                          return platform ? (
                            <Badge key={p} variant="outline" className="gap-1">
                              <platform.icon className="w-3 h-3" />
                              {platform.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openPreview(post)} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => duplicatePost(post)} title="Duplicate">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openRecurringModal(post)} title="Make Recurring">
                          <Repeat className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(post)} title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(post.post_id)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-stone-700 line-clamp-2 mb-2">{post.content}</p>
                    
                    {post.hashtags?.length > 0 && (
                      <p className="text-blue-600 text-sm mb-2">{post.hashtags.join(" ")}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-stone-500">
                      <div className="flex items-center gap-4">
                        {post.scheduled_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(post.scheduled_date)}
                          </span>
                        )}
                        {post.scheduled_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.scheduled_time}
                          </span>
                        )}
                      </div>
                      {post.status === "scheduled" && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-green-600 border-green-600"
                          onClick={() => markAsPublished(post.post_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Published
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Post" : "Create New Post"}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Templates Section */}
            {!editingPost && (
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">
                    {templates.length > 0 ? `${templates.length} template(s) available` : "No templates yet"}
                  </span>
                </div>
                <div className="flex gap-2">
                  {templates.length > 0 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowTemplates(!showTemplates)}
                      className="text-purple-600 border-purple-300"
                    >
                      Load Template
                    </Button>
                  )}
                  {formData.content && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={saveAsTemplate}
                      className="text-purple-600 border-purple-300"
                    >
                      Save as Template
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* Templates List */}
            {showTemplates && templates.length > 0 && (
              <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-white">
                {templates.map(tpl => (
                  <div 
                    key={tpl.template_id}
                    className="flex items-center justify-between p-2 hover:bg-stone-50 rounded-lg cursor-pointer group"
                  >
                    <div 
                      className="flex-1"
                      onClick={() => loadTemplate(tpl)}
                    >
                      <p className="font-medium text-stone-800">{tpl.name}</p>
                      <p className="text-xs text-stone-500 truncate">{tpl.content?.substring(0, 60)}...</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-stone-400">Used {tpl.use_count || 0}x</span>
                      <Button 
                        type="button"
                        size="sm" 
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100 text-red-500"
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.template_id); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your post content..."
                rows={4}
                required
              />
              <p className="text-xs text-stone-500 mt-1">{formData.content.length}/280 characters</p>
            </div>

            {/* Platforms */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Platforms</label>
              <div className="flex gap-2">
                {PLATFORMS.map(platform => (
                  <button
                    key={platform.id}
                    type="button"
                    onClick={() => togglePlatform(platform.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                      formData.platforms.includes(platform.id)
                        ? "border-amber-500 bg-amber-50"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <platform.icon className="w-5 h-5" />
                    <span className="text-sm">{platform.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Date</label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Time</label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Images Carousel (up to 4) */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Images {formData.images.length > 0 && `(${formData.images.length}/4)`}
                {formData.platforms.includes("instagram") && formData.images.length > 1 && (
                  <span className="ml-2 text-xs text-pink-600">Carousel for Instagram</span>
                )}
              </label>
              <div className="flex gap-2 flex-wrap">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border group">
                    <img 
                      src={`data:${img.mime_type};base64,${img.base64}`}
                      alt={`Image ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 left-0 bg-black/50 text-white text-xs px-1 rounded-br">
                      {idx + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {formData.images.length < 4 && (
                  <label className="flex items-center justify-center w-24 h-24 border-2 border-dashed rounded-lg cursor-pointer hover:border-amber-500 transition-colors">
                    <input type="file" accept="image/*" onChange={handleAddImage} className="hidden" />
                    <div className="text-center">
                      <Image className="w-6 h-6 mx-auto text-stone-400" />
                      <span className="text-xs text-stone-500">Add</span>
                    </div>
                  </label>
                )}
              </div>
              {formData.images.length > 1 && (
                <p className="text-xs text-stone-500 mt-1">
                  Drag to reorder • First image is the cover
                </p>
              )}
            </div>

            {/* Hashtags with Suggestions */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-stone-700">Hashtags</label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={fetchHashtagSuggestions}
                  disabled={loadingHashtags || !formData.content.trim()}
                  className="text-xs text-amber-600 h-6"
                >
                  {loadingHashtags ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Zap className="w-3 h-3 mr-1" />
                  )}
                  Suggest
                </Button>
              </div>
              <div className="flex gap-2 mb-2">
                <Input
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  placeholder="#CatPalsPlus"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addHashtag())}
                />
                <Button type="button" onClick={addHashtag} variant="outline">Add</Button>
              </div>
              
              {/* Suggested Hashtags */}
              {suggestedHashtags && (
                <div className="bg-amber-50 rounded-lg p-3 mb-3 space-y-3">
                  {suggestedHashtags.suggested?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-1">Suggested for your content:</p>
                      <div className="flex flex-wrap gap-1">
                        {suggestedHashtags.suggested.map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => addSuggestedHashtag(tag)}
                            disabled={formData.hashtags.includes(tag)}
                            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                              formData.hashtags.includes(tag)
                                ? "bg-green-100 text-green-700"
                                : "bg-white text-stone-700 hover:bg-amber-100 border"
                            }`}
                          >
                            {tag} {formData.hashtags.includes(tag) && "✓"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {suggestedHashtags.trending?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-1">Trending in your posts:</p>
                      <div className="flex flex-wrap gap-1">
                        {suggestedHashtags.trending.map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => addSuggestedHashtag(tag)}
                            disabled={formData.hashtags.includes(tag)}
                            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                              formData.hashtags.includes(tag)
                                ? "bg-green-100 text-green-700"
                                : "bg-white text-stone-700 hover:bg-amber-100 border"
                            }`}
                          >
                            {tag} {formData.hashtags.includes(tag) && "✓"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(suggestedHashtags.categories || {}).map(([category, tags]) => (
                      <div key={category}>
                        <p className="text-xs text-stone-500 capitalize mb-1">{category}:</p>
                        <div className="flex flex-wrap gap-1">
                          {tags.slice(0, 3).map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => addSuggestedHashtag(tag)}
                              disabled={formData.hashtags.includes(tag)}
                              className={`text-xs px-1.5 py-0.5 rounded transition-colors ${
                                formData.hashtags.includes(tag)
                                  ? "bg-green-100 text-green-700"
                                  : "bg-white text-stone-600 hover:bg-stone-100 border"
                              }`}
                            >
                              {tag.replace('#', '')}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Selected Hashtags */}
              {formData.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.hashtags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button type="button" onClick={() => removeHashtag(tag)}>
                        <XCircle className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Internal Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes for your team..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                {editingPost ? "Save Changes" : "Create Post"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Post Preview</DialogTitle>
          </DialogHeader>
          
          {previewPost && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {previewPost.platforms?.map(p => {
                  const platform = PLATFORMS.find(pl => pl.id === p);
                  return platform ? (
                    <Badge key={p} className={`${platform.color} text-white`}>
                      <platform.icon className="w-3 h-3 mr-1" />
                      {platform.name}
                    </Badge>
                  ) : null;
                })}
              </div>
              
              {/* Images Carousel Preview */}
              {(previewPost.image_base64 || previewPost.additional_images?.length > 0) && (
                <div className="rounded-lg overflow-hidden">
                  {previewPost.additional_images?.length > 0 ? (
                    <div className="relative">
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        <img 
                          src={`data:${previewPost.image_mime_type};base64,${previewPost.image_base64}`}
                          alt="Post 1"
                          className="w-48 h-48 object-cover rounded-lg flex-shrink-0"
                        />
                        {previewPost.additional_images.map((img, idx) => (
                          <img 
                            key={idx}
                            src={`data:${img.mime_type};base64,${img.base64}`}
                            alt={`Post ${idx + 2}`}
                            className="w-48 h-48 object-cover rounded-lg flex-shrink-0"
                          />
                        ))}
                      </div>
                      <div className="absolute bottom-4 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                        {1 + previewPost.additional_images.length} images
                      </div>
                    </div>
                  ) : previewPost.image_base64 && (
                    <img 
                      src={`data:${previewPost.image_mime_type};base64,${previewPost.image_base64}`}
                      alt="Post"
                      className="w-full rounded-lg"
                    />
                  )}
                </div>
              )}
              
              <p className="text-stone-800 whitespace-pre-wrap">{previewPost.content}</p>
              
              {previewPost.hashtags?.length > 0 && (
                <p className="text-blue-600">{previewPost.hashtags.join(" ")}</p>
              )}
              
              {previewPost.scheduled_date && (
                <p className="text-sm text-stone-500">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Scheduled for {formatDate(previewPost.scheduled_date)} at {previewPost.scheduled_time || "TBD"}
                </p>
              )}
              
              {previewPost.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <FileText className="w-4 h-4 inline mr-1" />
                    <strong>Notes:</strong> {previewPost.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Bulk Upload Posts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-stone-50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">CSV Format:</p>
              <code className="block bg-white p-2 rounded border text-xs">
                content,platforms,date,time,hashtags,notes<br/>
                "Good morning cat lovers!",facebook instagram,2026-09-08,09:00,#CatPals #Cats,Morning post
              </code>
              <p className="text-stone-500 mt-2">
                Platforms: facebook, instagram, x (comma-separated)<br/>
                Date format: YYYY-MM-DD
              </p>
            </div>
            <Textarea
              value={bulkCsvText}
              onChange={(e) => setBulkCsvText(e.target.value)}
              placeholder="Paste your CSV data here..."
              rows={10}
              className="font-mono text-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpload(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpload} 
              disabled={uploadingBulk}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {uploadingBulk ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Posts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Posts Modal */}
      <Dialog open={showRecurring} onOpenChange={setShowRecurring}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-amber-600" />
              Create Recurring Posts
            </DialogTitle>
          </DialogHeader>
          {recurringPost && (
            <div className="space-y-4">
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-sm text-stone-600 line-clamp-2">{recurringPost.content}</p>
                <p className="text-xs text-stone-400 mt-1">
                  Starting from: {recurringPost.scheduled_date || "Today"}
                </p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Frequency</label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      variant={recurringFrequency === "weekly" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRecurringFrequency("weekly")}
                      className={recurringFrequency === "weekly" ? "bg-amber-600" : ""}
                    >
                      Weekly
                    </Button>
                    <Button
                      variant={recurringFrequency === "monthly" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRecurringFrequency("monthly")}
                      className={recurringFrequency === "monthly" ? "bg-amber-600" : ""}
                    >
                      Monthly
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Number of Occurrences</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={recurringOccurrences}
                      onChange={(e) => setRecurringOccurrences(Math.min(12, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-20"
                    />
                    <span className="text-sm text-stone-500">
                      (Creates posts for {recurringOccurrences} {recurringFrequency === "weekly" ? "weeks" : "months"})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurring(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRecurring} 
              disabled={creatingRecurring}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {creatingRecurring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Repeat className="w-4 h-4 mr-2" />
              )}
              Create {recurringOccurrences} Posts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Preview Modal */}
      <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-amber-600" />
              Post Analytics & Best Times
            </DialogTitle>
          </DialogHeader>
          
          {loadingAnalytics ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : analyticsData && (
            <div className="space-y-6">
              {/* Best Times by Platform */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Best Posting Times
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(analyticsData.best_times || {}).map(([platform, data]) => (
                    <Card key={platform} className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {platform === "facebook" && <Facebook className="w-5 h-5 text-blue-600" />}
                        {platform === "instagram" && <Instagram className="w-5 h-5 text-pink-600" />}
                        {platform === "x" && <XIcon className="w-5 h-5" />}
                        <span className="font-medium capitalize">{platform}</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-stone-500">Best Days:</span>
                          <p className="font-medium">{data.best_days?.join(", ")}</p>
                        </div>
                        <div>
                          <span className="text-stone-500">Best Times:</span>
                          <p className="font-medium">{data.best_times?.join(", ")}</p>
                        </div>
                        <div className="pt-2 border-t">
                          <span className="text-stone-500">Est. Reach:</span>
                          <p className="font-medium text-green-600">{data.estimated_reach}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Your Stats */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  Your Posting History
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{analyticsData.your_stats?.total_published || 0}</p>
                    <p className="text-sm text-stone-500">Posts Published</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{analyticsData.your_stats?.by_platform?.facebook || 0}</p>
                    <p className="text-sm text-stone-500">Facebook</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <p className="text-2xl font-bold text-pink-600">{analyticsData.your_stats?.by_platform?.instagram || 0}</p>
                    <p className="text-sm text-stone-500">Instagram</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <p className="text-2xl font-bold text-stone-800">{analyticsData.your_stats?.by_platform?.x || 0}</p>
                    <p className="text-sm text-stone-500">X (Twitter)</p>
                  </Card>
                </div>
              </div>

              {/* Engagement Scores */}
              {analyticsData.posts_preview?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <BarChart2 className="w-4 h-4 text-amber-500" />
                    Upcoming Posts - Engagement Scores
                  </h3>
                  <div className="space-y-2">
                    {analyticsData.posts_preview.map((post) => (
                      <div key={post.post_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium line-clamp-1">{post.content_preview}</p>
                          <p className="text-xs text-stone-500">
                            {post.scheduled_date} at {post.scheduled_time} • {post.platforms?.join(", ")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            post.engagement_score >= 80 ? 'bg-green-100 text-green-700' :
                            post.engagement_score >= 60 ? 'bg-blue-100 text-blue-700' :
                            post.engagement_score >= 40 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {post.engagement_score}% - {post.score_label}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSocialPostsTab;
